import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { PageSpinner, Modal, EmptyState } from "../components/ui.js";
import { IconClipboard } from "../components/icons.js";

interface Fact {
  id: string;
  category: string;
  label: string;
  value: string | null;
  knowledgeState: string;
  sensitivity: string;
  aiAccessLevel: string;
  status: string;
  whyItMatters: string | null;
}

interface Medication {
  id: string;
  name: string;
  kind: string;
  strength: string | null;
  dose: string | null;
  frequency: string | null;
  schedule: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  personal: "Personal",
  body: "Body",
  health: "Health",
  recovery: "Surgery & Recovery",
  mental_health: "Mental Health",
  pain: "Pain & Mobility",
  sleep: "Sleep",
  household: "Household",
  food_environment: "Food Environment",
  food: "Food & Preferences",
  work: "Work",
  caregiving: "Caregiving",
  activity: "Activity",
  training: "Training",
  goals: "Goals",
  motivation: "Motivation",
  coaching: "Coaching"
};

const KNOWLEDGE_LABELS: Record<string, string> = {
  confirmed_fact: "Confirmed",
  current_state: "Current",
  preference: "Preference",
  observation: "Observation",
  user_hypothesis: "Your suspicion",
  ai_inference: "AI guess",
  medically_documented: "Medical",
  calculated: "Calculated",
  estimated: "Estimated",
  imported: "Imported"
};

const AI_ACCESS_LABELS: Record<string, string> = {
  any_provider: "AI: any",
  blueprint_only: "AI: private only",
  never_send: "AI: never",
  archived: "Archived"
};

const KNOWLEDGE_OPTIONS = Object.entries(KNOWLEDGE_LABELS);
const AI_ACCESS_OPTIONS = Object.entries(AI_ACCESS_LABELS);
const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS);

export function LifeProfilePage() {
  const [byCategory, setByCategory] = useState<Record<string, Fact[]> | null>(null);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [candidates, setCandidates] = useState<Fact[]>([]);
  const [editing, setEditing] = useState<Fact | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const [facts, meds, cands] = await Promise.all([
        api.get<{ byCategory: Record<string, Fact[]> }>("/api/profile/facts"),
        api.get<{ medications: Medication[] }>("/api/medications"),
        api.get<{ candidates: Fact[] }>("/api/profile/candidates")
      ]);
      setByCategory(facts.byCategory);
      setMedications(meds.medications);
      setCandidates(cands.candidates as unknown as Fact[]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load your profile.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function resolveCandidate(id: string, action: "accept" | "reject") {
    setCandidates((prev) => prev.filter((c) => c.id !== id));
    await api.post(`/api/profile/candidates/${id}/${action}`).catch(() => undefined);
    if (action === "accept") await load();
  }

  if (!byCategory) {
    return (
      <div>
        <div className="page-header">
          <h1>What ClovaForm knows about you</h1>
        </div>
        {error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />}
      </div>
    );
  }

  const categories = [
    ...CATEGORY_ORDER.filter((c) => byCategory[c]?.length),
    ...Object.keys(byCategory).filter((c) => !CATEGORY_ORDER.includes(c) && byCategory[c]?.length)
  ];
  const totalFacts = Object.values(byCategory).reduce((n, f) => n + f.length, 0);

  return (
    <div>
      <div className="page-header">
        <h1>What ClovaForm knows about you</h1>
        <p className="subtitle">
          Everything here is yours to correct. ClovaForm keeps how certain each
          thing is, and you control what it may share with AI.
        </p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {candidates.length > 0 && (
        <div className="card mt-4" style={{ borderColor: "var(--accent)" }}>
          <div className="card-header">
            <span className="card-title">{candidates.length} thing(s) to confirm</span>
          </div>
          <div className="stack">
            {candidates.map((c) => (
              <div className="row-between wrap" key={c.id} style={{ gap: "var(--space-2)" }}>
                <div className="grow">
                  <div style={{ fontWeight: 600 }}>{c.label}</div>
                  {c.value && <div className="muted text-sm">{c.value}</div>}
                </div>
                <div className="row">
                  <button className="btn btn-sm" onClick={() => resolveCandidate(c.id, "accept")}>
                    Save
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => resolveCandidate(c.id, "reject")}>
                    Skip
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {totalFacts === 0 && candidates.length === 0 && (
        <EmptyState icon={<IconClipboard />} title="Nothing here yet">
          Complete onboarding, or add facts, and ClovaForm’s understanding of you
          will appear here.
        </EmptyState>
      )}

      {categories.map((category) => (
        <div className="card mt-4" key={category} style={{ padding: 0, overflow: "hidden" }}>
          <div className="nav-section-label" style={{ padding: "var(--space-4) var(--space-4) var(--space-2)" }}>
            {CATEGORY_LABELS[category] ?? category}
          </div>
          {byCategory[category]!.map((fact) => (
            <button
              key={fact.id}
              className="fact-row"
              onClick={() => setEditing(fact)}
              type="button"
            >
              <div className="grow" style={{ textAlign: "left" }}>
                <div style={{ fontWeight: 600 }}>{fact.label}</div>
                {fact.value && <div className="muted text-sm">{fact.value}</div>}
                <div className="row mt-2" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
                  <span className="badge badge-muted">{KNOWLEDGE_LABELS[fact.knowledgeState] ?? fact.knowledgeState}</span>
                  {fact.sensitivity === "critical" || fact.sensitivity === "high" ? (
                    <span className="badge badge-warning">sensitive</span>
                  ) : null}
                  <span className="badge badge-muted">{AI_ACCESS_LABELS[fact.aiAccessLevel]}</span>
                  {fact.status !== "active" && <span className="badge badge-muted">{fact.status}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      ))}

      {medications.length > 0 && (
        <div className="card mt-4">
          <div className="card-header">
            <span className="card-title">Medications & supplements</span>
          </div>
          <p className="muted text-xs">
            ClovaForm never recommends starting, stopping, or changing a
            medication — that’s between you and your clinician. Changes keep history.
          </p>
          <div className="table-cards mt-4">
            {medications.map((m) => (
              <div className="table-card" key={m.id}>
                <div style={{ fontWeight: 600 }}>{m.name}</div>
                <div className="dim text-xs">
                  {[m.kind, m.strength, m.dose, m.frequency, m.schedule === "prn" ? "as needed" : null]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <FactEditor
          fact={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function FactEditor({
  fact,
  onClose,
  onSaved
}: {
  fact: Fact;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(fact.value ?? "");
  const [knowledgeState, setKnowledgeState] = useState(fact.knowledgeState);
  const [aiAccessLevel, setAiAccessLevel] = useState(fact.aiAccessLevel);
  const [status, setStatus] = useState(fact.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/api/profile/facts/${fact.id}`, {
        value,
        knowledgeState,
        aiAccessLevel,
        status
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    try {
      await api.del(`/api/profile/facts/${fact.id}`);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete.");
      setSaving(false);
    }
  }

  return (
    <Modal title={fact.label} onClose={onClose}>
      {fact.whyItMatters && (
        <div className="alert alert-info text-sm">{fact.whyItMatters}</div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="field">
        <label>Value</label>
        <textarea className="textarea" value={value} onChange={(e) => setValue(e.target.value)} rows={3} />
      </div>
      <div className="field">
        <label>How certain is this?</label>
        <select className="select" value={knowledgeState} onChange={(e) => setKnowledgeState(e.target.value)}>
          {KNOWLEDGE_OPTIONS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>AI access</label>
        <select className="select" value={aiAccessLevel} onChange={(e) => setAiAccessLevel(e.target.value)}>
          {AI_ACCESS_OPTIONS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <span className="hint">Controls whether this fact can be sent to AI, and to which providers.</span>
      </div>
      <div className="field">
        <label>Status</label>
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">Active</option>
          <option value="outdated">Mark outdated</option>
          <option value="archived">Archive</option>
        </select>
      </div>
      <div className="row mt-4">
        <button className="btn btn-primary grow" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button className="btn btn-danger" onClick={remove} disabled={saving}>
          Delete
        </button>
      </div>
    </Modal>
  );
}
